import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AgentForm from './AgentForm';
import AgentWizard from './AgentWizard/AgentWizard';
import AgentTestModal from './AgentTestModal';
import AgentService from '../../services/agentService';
import { supabase } from '../../config/supabase';

interface Agent {
  agent_id: string;
  name: string;
  created_at_unix_secs?: number;
  access_info?: {
    is_creator: boolean;
    creator_name: string;
    creator_email: string;
    role: string;
  };
}

interface AgentListProps {
  apiKey: string;
}

const AgentList: React.FC<AgentListProps> = ({ apiKey }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [testingAgent, setTestingAgent] = useState<Agent | null>(null);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importAgentId, setImportAgentId] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchAgents = useCallback(async () => {
    if (!apiKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const agentService = new AgentService(apiKey);
      const userAgents = await agentService.getUserAgents();
      setAgents(userAgents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const removeAgent = async (agentId: string) => {
    if (!window.confirm('Are you sure you want to remove this agent from your account? The agent will not be deleted from ElevenLabs.')) return;

    try {
      const agentService = new AgentService(apiKey);
      const success = await agentService.removeAgent(agentId);
      
      if (success) {
        await fetchAgents(); // Refresh the list
      } else {
        setError('Failed to remove agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove agent');
    }
  };

  const handleCreateAgent = () => {
    setEditingAgent(null);
    setShowForm(true);
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAgent(null);
    fetchAgents();
  };

  const handleTestClose = () => {
    setTestingAgent(null);
  };

  const importAgent = async () => {
    if (!importAgentId.trim()) {
      setError('Agent ID is required');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // First, check if the agent exists in ElevenLabs by trying to fetch it
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${importAgentId}`, {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError('Agent not found. Please check the Agent ID.');
        } else {
          setError('Failed to verify agent. Please check your API key and try again.');
        }
        return;
      }

      const agentDetails = await response.json();

      // Add the agent to user's agents list in the database
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        setError('User not authenticated');
        return;
      }

      // Check if agent is already added to avoid duplicates
      const { data: existingAgent } = await supabase
        .from('agents')
        .select('*')
        .eq('user_id', user.id)
        .eq('elevenlabs_agent_id', importAgentId)
        .single();

      if (existingAgent) {
        setError('This agent is already in your account');
        return;
      }

      // Add the agent to the user's account
      const { error: insertError } = await supabase
        .from('agents')
        .insert({
          user_id: user.id,
          elevenlabs_agent_id: importAgentId,
          name: agentDetails.name,
          local_settings: {}
        });

      if (insertError) {
        setError('Failed to add agent to your account');
        return;
      }

      // Close the import form and refresh agents list
      setShowImportForm(false);
      setImportAgentId('');
      await fetchAgents();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import agent');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFormClose = () => {
    setShowImportForm(false);
    setImportAgentId('');
    setError(null);
  };

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  if (loading && agents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-300 text-lg">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {error && (
        <div className="glass-card rounded-2xl p-4 mb-6 border border-red-500/30 neon-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">❌</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Your Agents</h2>
          <p className="text-gray-400 text-sm sm:text-base">Manage your personal voice agents and track performance</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => setShowImportForm(true)}
            className="btn-secondary flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <span>📥</span>
            <span>Import Agent</span>
          </button>
          <button
            onClick={handleCreateAgent}
            className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <span>+</span>
            <span>Create Agent</span>
          </button>
        </div>
      </div>

      {!apiKey ? (
        <div className="glass-card rounded-2xl p-8 text-center border border-yellow-500/30">
          <div className="text-6xl mb-4">🔑</div>
          <h3 className="text-xl font-semibold text-yellow-300 mb-2">API Key Required</h3>
          <p className="text-gray-300">
            Please configure your API key in the environment variables to manage agents.
          </p>
        </div>
      ) : agents.length === 0 ? (
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="text-6xl mb-4">🤖</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No agents found</h3>
          <p className="text-gray-400 mb-6">
            You haven't created any agents yet. Create your first AI agent to get started!
          </p>
          <button
            onClick={handleCreateAgent}
            className="btn-primary"
          >
            🚀 Create Your First Agent
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {agents.map((agent) => (
            <div
              key={agent.agent_id}
              className="glass-card rounded-2xl p-4 sm:p-6 card-hover"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg sm:text-2xl">🤖</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-white truncate">{agent.name}</h3>
                      <span className="bg-green-500/20 text-green-300 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm border border-green-500/30 w-fit mt-1 sm:mt-0">
                        Active
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm font-mono mb-2 break-all">ID: {agent.agent_id}</p>
                    
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-4 space-y-1 sm:space-y-0 text-xs sm:text-sm">
                      {agent.created_at_unix_secs && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400">📅 Created:</span>
                          <span className="text-white">
                            {new Date(agent.created_at_unix_secs * 1000).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      
                      {agent.access_info && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400">👤 Role:</span>
                          <span className="text-blue-300 capitalize">{agent.access_info.role}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-row lg:flex-col lg:items-end space-x-2 lg:space-x-0 lg:space-y-3">
                                      <button
                     onClick={() => setTestingAgent(agent)}
                     className="flex-1 lg:flex-none glass-effect hover:bg-green-500/20 border border-green-500/30 text-green-300 hover:text-green-200 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                  >
                     <span>🎤</span>
                     <span>Test</span>
                  </button>

                  <button
                     onClick={() => handleEditAgent(agent)}
                    className="flex-1 lg:flex-none glass-effect hover:bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:text-blue-200 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                  >
                    <span>✏️</span>
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => removeAgent(agent.agent_id)}
                    className="flex-1 lg:flex-none glass-effect hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                  >
                    <span>➖</span>
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && editingAgent && (
        <AgentForm
          apiKey={apiKey}
          agent={editingAgent}
          onClose={handleFormClose}
        />
      )}

      {showForm && !editingAgent && (
        <AgentWizard
          apiKey={apiKey}
          onClose={handleFormClose}
          onComplete={handleFormClose}
        />
      )}

      {testingAgent && (
        <AgentTestModal
          agentId={testingAgent.agent_id}
          agentName={testingAgent.name}
          onClose={handleTestClose}
        />
      )}

      {/* Import Agent Modal */}
      {showImportForm && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-2xl max-w-md w-full border border-white/10">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Import Agent</h2>
                  <p className="text-gray-400">Add an existing agent to your account by ID</p>
                </div>
                <button
                  onClick={handleImportFormClose}
                  className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="agentId" className="block text-sm font-medium text-gray-300 mb-2">
                    Agent ID *
                  </label>
                  <input
                    type="text"
                    id="agentId"
                    value={importAgentId}
                    onChange={(e) => setImportAgentId(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Enter Agent ID"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Find the Agent ID in your platform dashboard under agent details
                  </p>
                </div>

                <div className="glass-effect rounded-xl p-4 border border-blue-500/30">
                  <div className="flex items-start space-x-3">
                    <span className="text-blue-400 text-xl mt-1">💡</span>
                    <div>
                      <h5 className="text-white font-medium mb-2">How to find Agent ID</h5>
                      <div className="text-gray-300 text-sm space-y-1">
                        <p>1. Go to your platform dashboard</p>
                        <p>2. Navigate to the Conversational AI section</p>
                        <p>3. Click on an agent to view its details</p>
                        <p>4. Copy the Agent ID from the agent information panel</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-8 border-t border-white/10 mt-8">
                <button
                  onClick={handleImportFormClose}
                  className="btn-secondary px-6 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={importAgent}
                  disabled={importing || !importAgentId.trim()}
                  className="btn-primary px-6 py-3 disabled:opacity-50"
                >
                  {importing ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Importing...</span>
                    </div>
                  ) : (
                    '📥 Import Agent'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AgentList; 